import { useEffect, useState } from 'react';
import { rentalsApi } from '../../api/rentals.js';
import { Badge } from '../ui/Badge.jsx';
import { Copyable } from '../ui/Copyable.jsx';
import { Modal } from '../ui/Modal.jsx';
import { formatDate } from '../../utils/dates.js';
import { RentalForm } from './RentalForm.jsx';
import { RentalDetailModal } from './RentalDetailModal.jsx';
import { SignContractModal } from './SignContractModal.jsx';

function CloseDialog({ open, onClose, rental, onClosed }) {
  const [kmEnd, setKmEnd] = useState('');
  const [dataEnd, setDataEnd] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open) {
      setKmEnd(rental?.km_start ? String(rental.km_start) : '');
      setDataEnd(new Date().toISOString().slice(0, 10));
      setErr('');
    }
  }, [open, rental]);

  async function handleSubmit(e) {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      const body = { data_end: dataEnd };
      if (kmEnd !== '' && kmEnd != null) body.km_end = Number(kmEnd);
      if (rental.km_start && body.km_end != null && body.km_end < rental.km_start) {
        setErr(`KM la retur (${body.km_end}) nu poate fi mai mic decât KM start (${rental.km_start}).`);
        setBusy(false);
        return;
      }
      await rentalsApi.close(rental.id, body);
      onClosed?.();
      onClose?.();
    } catch (ex) {
      setErr(ex.body?.error || 'Eroare');
    } finally {
      setBusy(false);
    }
  }

  if (!rental) return null;

  return (
    <Modal open={open} onClose={onClose} title="Închide închirierea">
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-muted">
          Chiriaș: <span className="font-semibold text-ink">{rental.chirias_nume}</span>
          {rental.km_start != null && <> · start {rental.km_start.toLocaleString('ro-RO')} km</>}
        </p>

        {err && <div className="rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">{err}</div>}

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted">Data retur</span>
            <input
              type="date"
              value={dataEnd}
              onChange={(e) => setDataEnd(e.target.value)}
              className="mt-1 w-full rounded-lg border border-border bg-paper px-3 py-2.5 focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20"
            />
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted">KM la retur</span>
            <input
              type="number"
              min={rental.km_start || 0}
              value={kmEnd}
              onChange={(e) => setKmEnd(e.target.value)}
              placeholder="km"
              className="mt-1 w-full rounded-lg border border-border bg-paper px-3 py-2.5 focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20"
            />
          </label>
        </div>

        <p className="text-xs text-muted">
          Dacă introduci KM la retur, se actualizează automat kilometrajul mașinii.
        </p>

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-ghost">Anulează</button>
          <button type="submit" disabled={busy} className="btn-primary">
            {busy ? 'Se închide…' : 'Confirmă închiderea'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function RentalRow({ rental, onRefresh, onRequestClose, onOpenDetails, onSign }) {
  async function handleDelete() {
    if (!confirm('Ștergi închirierea? Fișierele ID și permis se șterg.')) return;
    try {
      await rentalsApi.remove(rental.id);
      onRefresh?.();
    } catch (ex) {
      alert('Eroare: ' + (ex.body?.error || ex.message));
    }
  }

  const kmSpan =
    rental.km_start != null || rental.km_end != null ? (
      <span className="font-mono">
        {rental.km_start != null ? rental.km_start.toLocaleString('ro-RO') : '—'} km
        {rental.km_end != null && ` → ${rental.km_end.toLocaleString('ro-RO')} km`}
      </span>
    ) : null;

  return (
    <div className={`card ${rental.activ ? '' : 'opacity-75'}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => onOpenDetails(rental)}
              className="text-base font-semibold text-ink hover:text-teal-bright underline-offset-2 hover:underline"
              title="Click pentru detalii"
            >
              {rental.chirias_nume}
            </button>
            {rental.activ ? <Badge tone="ok">Activă</Badge> : <Badge tone="muted">Închisă</Badge>}
          </div>

          {rental.chirias_telefon && (
            <Copyable
              value={rental.chirias_telefon}
              as="button"
              className="mt-1 block font-mono text-xs text-muted hover:text-ink"
              title="Click pentru a copia telefonul"
            >
              {rental.chirias_telefon}
            </Copyable>
          )}

          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 font-mono text-xs text-muted">
            <span>
              {formatDate(rental.data_start)} → {rental.data_end ? formatDate(rental.data_end) : 'în curs'}
            </span>
            {kmSpan}
          </div>

          {rental.observatii && <p className="mt-2 text-sm text-muted">{rental.observatii}</p>}

          {/* Butoane documente + contract */}
          <div className="mt-3 flex flex-wrap gap-2">
            {rental.id_document_path && (
              <a
                href={rentalsApi.fileUrl(rental.id, 'id')}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-lg border border-teal/20 bg-teal/5 px-3 py-1.5 text-xs font-semibold text-teal-bright hover:bg-teal/10"
              >
                <span aria-hidden>📄</span> CI
              </a>
            )}
            {rental.permis_document_path && (
              <a
                href={rentalsApi.fileUrl(rental.id, 'permis')}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-lg border border-teal/20 bg-teal/5 px-3 py-1.5 text-xs font-semibold text-teal-bright hover:bg-teal/10"
              >
                <span aria-hidden>📄</span> Permis
              </a>
            )}
            {rental.contract_signed_at ? (
              <a
                href={rentalsApi.fileUrl(rental.id, 'contract')}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-lg border border-ok/30 bg-ok/10 px-3 py-1.5 text-xs font-semibold text-ok hover:bg-ok/20"
              >
                <span aria-hidden>✓</span> Contract semnat
              </a>
            ) : rental.contract_path ? (
              <>
                <a
                  href={rentalsApi.fileUrl(rental.id, 'contract')}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-warn/30 bg-warn/10 px-3 py-1.5 text-xs font-semibold text-warn hover:bg-warn/20"
                >
                  📄 Contract (așteaptă semnare)
                </a>
                <button
                  onClick={() => onSign(rental)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-coral/30 bg-coral/5 px-3 py-1.5 text-xs font-semibold text-coral hover:bg-coral/10"
                >
                  ↻ Retrimite SMS
                </button>
              </>
            ) : (
              rental.activ && (
                <button
                  onClick={() => onSign(rental)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-coral/30 bg-coral/5 px-3 py-1.5 text-xs font-semibold text-coral hover:bg-coral/10"
                >
                  ✎ Generează contract + SMS
                </button>
              )
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {rental.activ && (
            <button
              onClick={() => onRequestClose(rental)}
              className="btn bg-ink text-cream hover:bg-ink-soft text-xs"
            >
              Închide
            </button>
          )}
          <button onClick={handleDelete} className="btn-ghost text-xs text-danger hover:bg-danger/10">
            Șterge
          </button>
        </div>
      </div>
    </div>
  );
}

export function RentalsTab({ carId, carKm, car }) {
  const [rentals, setRentals] = useState(null);
  const [err, setErr] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [closing, setClosing] = useState(null);
  const [detailRental, setDetailRental] = useState(null);
  const [signing, setSigning] = useState(null);

  async function load() {
    try {
      const { rentals } = await rentalsApi.listForCar(carId);
      setRentals(rentals);
    } catch (ex) {
      setErr(ex.body?.error || 'Eroare');
    }
  }

  useEffect(() => {
    load();
  }, [carId]);

  const active = (rentals || []).filter((r) => r.activ);
  const past = (rentals || []).filter((r) => !r.activ);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted">
          {rentals ? `${active.length} activ${active.length === 1 ? 'ă' : 'e'}` : ''}
          {past.length > 0 && <span className="text-muted/70"> · {past.length} în istoric</span>}
        </span>
        <button onClick={() => setAddOpen(true)} className="btn-primary text-sm">+ Închiriere nouă</button>
      </div>

      {err && <div className="rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">{err}</div>}

      {rentals === null && <div className="card text-center text-muted">Se încarcă…</div>}

      {rentals && rentals.length === 0 && (
        <div className="card text-center text-sm text-muted">
          Nu există închirieri înregistrate.
        </div>
      )}

      {active.length > 0 && (
        <div className="space-y-2">
          {active.map((r) => <RentalRow key={r.id} rental={r} onRefresh={load} onRequestClose={setClosing} onOpenDetails={setDetailRental} onSign={setSigning} />)}
        </div>
      )}

      {past.length > 0 && (
        <details className="mt-4">
          <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-muted hover:text-ink">
            Istoric ({past.length})
          </summary>
          <div className="mt-2 space-y-2">
            {past.map((r) => <RentalRow key={r.id} rental={r} onRefresh={load} onRequestClose={setClosing} onOpenDetails={setDetailRental} onSign={setSigning} />)}
          </div>
        </details>
      )}

      <RentalForm
        open={addOpen}
        onClose={() => setAddOpen(false)}
        carId={carId}
        carKm={carKm}
        onCreated={load}
      />
      <CloseDialog
        open={Boolean(closing)}
        onClose={() => setClosing(null)}
        rental={closing}
        onClosed={load}
      />
      <RentalDetailModal
        open={Boolean(detailRental)}
        onClose={() => setDetailRental(null)}
        rental={detailRental}
      />
      <SignContractModal
        open={Boolean(signing)}
        onClose={() => setSigning(null)}
        rental={signing}
        car={car}
        onSigned={load}
      />
    </div>
  );
}
